import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const styles = {
    indented: {
        marginLeft: '2em',
        marginBottom: '.5em',
    },
    label: {
        margin: '0 1em 0 .5em',
    },
};

const RawStartRelative = ({
    reportSettings,
    onChange,
    startRelative,
    days,
    hours,
    minutes,
    handleChangeStartRelative,
    handleChangeDays,
    handleChangeHours,
    handleChangeMinutes,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <Fragment>
        <div>
            <label>
                <input
                    type="radio"
                    name="manual-data-start"
                    id="manual-data-start-relative"
                    checked={ startRelative }
                    onChange={ handleChangeStartRelative }
                />
                { t('6.1241.0.87') }
            </label>
        </div>
        <div style={ styles.indented }>
            <Input
                type="number"
                id="manual-data-start-days"
                value={ days }
                onChange={ handleChangeDays }
                min="0"
                max="999"
                maxLength={ 3 }
            />
            <label htmlFor="manual-data-start-days" style={ styles.label }>
                { t('WC_REPORT_SCHEDULE_DAY', { count: parseInt(days, 10) }) }
            </label>
            <Input
                type="number"
                id="manual-data-start-hours"
                value={ hours }
                onChange={ handleChangeHours }
                min="0"
                max="23"
                maxLength={ 2 }
            />
            <label htmlFor="manual-data-start-hours" style={ styles.label }>
                { t('WC_REPORT_SCHEDULE_HOUR', { count: parseInt(hours, 10) }) }
            </label>
            <Input
                type="number"
                id="manual-data-start-minutes"
                value={ minutes }
                onChange={ handleChangeMinutes }
                min="0"
                max="59"
                maxLength={ 2 }
            />
            <label htmlFor="manual-data-start-minutes" style={ styles.label }>
                { t('WC_REPORT_SCHEDULE_MINUTE', { count: parseInt(minutes, 10) }) }
            </label>
        </div>
    </Fragment>
;

type ChangeHandler = React.ChangeEventHandler<HTMLInputElement>;

type RecomposeProps = {
    startRelative: boolean;
    days: string;
    hours: string;
    minutes: string;
    handleChangeStartRelative: ChangeHandler;
    handleChangeDays: ChangeHandler;
    handleChangeHours: ChangeHandler;
    handleChangeMinutes: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setDays: (s: string) => void;
    _setMinutes: (s: string) => void;
    _setHours: (s: string) => void;
};

const parseStart = (reportSettings: Readonly<ReportSettings>, what: 'days' | 'hours' | 'minutes'): string => {
    const { start } = reportSettings;
    switch (what) {
        case 'days':
            if (start > 0) {
                return '1';
            }
            return Math.floor(start / -1440).toString();

        case 'hours':
            if (start > 0) {
                return '0';
            }
            return Math.floor((-start % 1440) / 60).toString();

        case 'minutes':
            if (start > 0) {
                return '0';
            }
            return (-start % 60).toString();
    }
};

const bound = (min: number, value: number, max: number): number =>
    Math.max(min, Math.min(value, max)) || min
;

const updateStart = ({ onChange, days, hours, minutes }: OwnProps & _RecomposeProps) => {
    onChange(reportSettings => {
        const { end } = reportSettings;
        let { start } = reportSettings;
        start = parseInt(days, 10) * -1440 + parseInt(hours, 10) * -60 - parseInt(minutes, 10);
        return reportSettings.setStart(start).setEnd(end);
    });
};

export const StartRelative = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('StartRelative'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        startRelative: reportSettings.start < 0,
    })),
    withState('days', '_setDays', ({ reportSettings }: OwnProps) => parseStart(reportSettings, 'days')),
    withState('hours', '_setHours', ({ reportSettings }: OwnProps) => parseStart(reportSettings, 'hours')),
    withState('minutes', '_setMinutes', ({ reportSettings }: OwnProps) => parseStart(reportSettings, 'minutes')),
    withHandlers({
        handleChangeStartRelative: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                updateStart(props);
            }
        },
        handleChangeDays: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const days = bound(0, parseInt(e.target.value, 10), 999).toString();
            if (days !== props.days) {
                props._setDays(days);
            }
            updateStart({ ...props, days });
        },
        handleChangeHours: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const hours = bound(0, parseInt(e.target.value, 10), 23).toString();
            if (hours !== props.hours) {
                props._setHours(hours);
            }
            updateStart({ ...props, hours });
        },
        handleChangeMinutes: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const minutes = bound(0, parseInt(e.target.value, 10), 59).toString();
            if (minutes !== props.minutes) {
                props._setMinutes(minutes);
            }
            updateStart({ ...props, minutes });
        },
    }),
)(RawStartRelative);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/dateRange/StartRelative.tsx